import React, { ReactNode } from "react"
import { HStack, Stack } from "@chakra-ui/react"
import { Control } from "react-hook-form"
import Select from "components/ReactHookForm/Select"
import TextField from "components/ReactHookForm/TextField"
import {
  WalletPassBuilderFormValues,
  PassFieldValueSource,
  PASS_FIELD_VALUE_OPTIONS,
} from "./walletPassBuilderTypes"
import { PassBuilderFieldRowLabel, PassBuilderHelpText } from "./PassBuilderTypography"
import PassBuilderFieldInput from "./PassBuilderFieldInput"
import { FIELD_CONTROL_STACK_SPACING, PASS_BUILDER_FIELD_VARIANT } from "./passBuilderTokens"

const inputProps = {
  variant: PASS_BUILDER_FIELD_VARIANT,
  fullWidth: true,
  mb: 0,
} as const

type FieldName = keyof WalletPassBuilderFormValues

type LabelRowProps = {
  name: FieldName
  control: Control<WalletPassBuilderFormValues>
  placeholder?: string
  /** Note under the input, for rows only Apple lets you label */
  help?: ReactNode
  disabled?: boolean
}

/** The Label: row every main field has. Editable while the field is on its
 *  default, except on the Header Field, whose label is the event's start time
 *  rather than decoration of the organizer's own. */
export const PassBuilderLabelRow = ({
  name, control, placeholder, help, disabled = false,
}: LabelRowProps) => (
  <Stack spacing={1}>
    <HStack spacing={0} align="center">
      <PassBuilderFieldRowLabel>Label:</PassBuilderFieldRowLabel>
      <PassBuilderFieldInput>
        <TextField
          name={name}
          control={control as any}
          placeholder={placeholder}
          disabled={disabled}
          {...inputProps}
        />
      </PassBuilderFieldInput>
    </HStack>
    {/* Outside the input's width so a line of it runs on rather than wrapping. */}
    {help && (
      <PassBuilderHelpText ml="53px" whiteSpace="nowrap">
        {help}
      </PassBuilderHelpText>
    )}
  </Stack>
)

type TextValueRowProps = {
  name: FieldName
  control: Control<WalletPassBuilderFormValues>
  placeholder?: string
  disabled?: boolean
}

/** Event Title and Event Location take free text — their only other choice is
 *  the event's own value, which the radios above handle. */
export const PassBuilderTextValueRow = ({
  name, control, placeholder, disabled = false,
}: TextValueRowProps) => (
  <HStack spacing={0} align="center">
    <PassBuilderFieldRowLabel>Value:</PassBuilderFieldRowLabel>
    <PassBuilderFieldInput>
      <TextField
        name={name}
        control={control as any}
        placeholder={placeholder}
        disabled={disabled}
        {...inputProps}
      />
    </PassBuilderFieldInput>
  </HStack>
)

type SourceValueRowProps = {
  sourceName: FieldName
  staticValueName: FieldName
  currentSource: PassFieldValueSource
  control: Control<WalletPassBuilderFormValues>
  options?: typeof PASS_FIELD_VALUE_OPTIONS
  staticValuePlaceholder?: string
  disabled?: boolean
}

/** A Value: dropdown with the text box that Static Text needs under it. */
export const PassBuilderSourceValueRow = ({
  sourceName,
  staticValueName,
  currentSource,
  control,
  options = PASS_FIELD_VALUE_OPTIONS,
  staticValuePlaceholder,
  disabled = false,
}: SourceValueRowProps) => (
  <Stack spacing={FIELD_CONTROL_STACK_SPACING}>
    <HStack spacing={0} align="center">
      <PassBuilderFieldRowLabel>Value:</PassBuilderFieldRowLabel>
      <PassBuilderFieldInput>
        <Select
          name={sourceName}
          control={control as any}
          options={options}
          disabled={disabled}
          {...inputProps}
        />
      </PassBuilderFieldInput>
    </HStack>
    {currentSource === "static" && (
      <PassBuilderFieldInput indented>
        <TextField
          name={staticValueName}
          control={control as any}
          placeholder={staticValuePlaceholder}
          disabled={disabled}
          {...inputProps}
        />
      </PassBuilderFieldInput>
    )}
  </Stack>
)
